import React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { Checkbox } from '../../components/ui/Checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { PageHeader } from '../../components/shared/PageHeader';
import { LoadingState } from '../../components/shared/LoadingState';
import { ErrorState } from '../../components/shared/ErrorState';
import { useAuth } from '../../core/contexts/AuthContext';
import { useClients } from '../../core/hooks/useClients';
import { useCurrentOrganization } from '../../core/hooks/useCurrentOrganization';
import { useDocuments } from '../../core/hooks/useDocuments';
import { UserRole } from '../../core/models/User';

type LogoSource = 'organization' | 'client' | 'both' | 'none';

interface FormValues {
  title: string;
  clientId: string;
  markdown: string;
  useOrgLogo: boolean;
  useClientLogo: boolean;
  description?: string;
}

export const MarkdownDocumentCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, organizationId } = useAuth();
  const isSuperAdmin = currentUser?.role === UserRole.SUPER_ADMIN;
  // For super admins, pass null to fetch all clients; otherwise use organizationId
  const effectiveOrgIdForClients = isSuperAdmin ? null : organizationId;
  const { clients, loading: clientsLoading, error: clientsError } = useClients(effectiveOrgIdForClients);
  const { createMarkdownShellDocument, loading: documentsLoading, error: documentsError } = useDocuments(
    null,
    organizationId
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      title: '',
      clientId: '',
      markdown: '',
      useOrgLogo: true,
      useClientLogo: false,
      description: '',
    },
    mode: 'onChange',
  });

  const selectedClientId = watch('clientId');
  const useOrgLogo = watch('useOrgLogo');
  const useClientLogo = watch('useClientLogo');
  const markdown = watch('markdown');
  const title = watch('title');

  // For super admins, use the selected client's organization for logo display
  const selectedClient = clients.find((c) => c.id === selectedClientId);
  const effectiveOrgIdForDisplay = isSuperAdmin && selectedClient?.organizationId 
    ? selectedClient.organizationId 
    : organizationId;
  
  // Always call hooks unconditionally - use the effective org ID
  const { organization: displayOrganization } = useCurrentOrganization(effectiveOrgIdForDisplay);

  if (!currentUser) {
    return <ErrorState message="You must be logged in to create documents." />;
  }

  if (clientsLoading || documentsLoading) {
    return <LoadingState message="Preparing document creator..." />;
  }

  if (clientsError || documentsError) {
    return <ErrorState message="Failed to load required data for document creation." />;
  }

  const onSubmit = async (data: FormValues) => {
    if (!data.clientId) {
      alert('Please select a project.');
      return;
    }

    if (!data.title || data.title.trim() === '') {
      alert('Please enter a document title.');
      return;
    }

    if (!data.markdown || data.markdown.trim() === '') {
      alert('Please enter some markdown content.');
      return;
    }

    // Determine effective organization for this document:
    // - For regular users: must have organizationId from auth
    // - For SUPER_ADMIN without organizationId: derive from selected client
    const selectedClient = clients.find(c => c.id === data.clientId);
    const effectiveOrganizationId =
      organizationId || (isSuperAdmin ? selectedClient?.organizationId : undefined);

    if (!effectiveOrganizationId) {
      alert(
        'Client ID is required. Please ensure you are logged in with a valid client account or select a project associated with a client account.'
      );
      return;
    }

    let logoSource: LogoSource = 'none';
    if (data.useOrgLogo && data.useClientLogo) {
      logoSource = 'both';
    } else if (data.useOrgLogo) {
      logoSource = 'organization';
    } else if (data.useClientLogo) {
      logoSource = 'client';
    }

    try {
      const result = await createMarkdownShellDocument({
        organizationId: effectiveOrganizationId,
        clientId: data.clientId,
        title: data.title.trim(),
        markdown: data.markdown,
        logoSource,
        description: data.description?.trim() || '',
        tags: ['markdown-shell'],
      });

      navigate(`/documents/${result.documentId}`);
    } catch (err: any) {
      console.error('Error creating markdown shell document:', err);
      alert(err?.message || 'Failed to create document. Please try again.');
    }
  };

  const orgHasLogo = !!displayOrganization?.branding?.logoUrl;
  const clientHasLogo = !!selectedClient?.logoUrl;

  const previewText =
    markdown && markdown.trim().length > 0
      ? markdown
      : 'Start typing markdown in the editor to see a preview here.\n\nHeadings, lists, and emphasis will render in the final PDF using the standard document shell.';

  return (
    <div className="space-y-6">
      <PageHeader
        title="Create Markdown Document"
        description="Create a lightweight document from markdown, rendered into the standard PDF shell."
        icon={FileText}
        actions={
          <Button variant="outline" onClick={() => navigate('/documents')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Documents
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Markdown Shell</CardTitle>
          <CardDescription>
            Choose a project, configure logo placement, and write your content in markdown. The PDF will use the same
            typography and layout as other HTML-based documents.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <Input
                  label="Document Title"
                  {...register('title', { required: 'Document title is required' })}
                  error={errors.title?.message}
                  placeholder="e.g., Implementation Notes - Q1 Rollout"
                />

                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Project <span className="text-error">*</span>
                  </label>
                  {clients.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No projects available. Please create a project first.
                    </p>
                  ) : (
                    <Select
                      value={watch('clientId') || ''}
                      onValueChange={(value) => setValue('clientId', value, { shouldValidate: true })}
                    >
                      <SelectTrigger className={errors.clientId ? 'border-error' : ''}>
                        <SelectValue placeholder="Select a project" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {errors.clientId && (
                    <p className="text-sm text-error">{errors.clientId.message as string}</p>
                  )}
                </div>

                <Textarea
                  label="Short Description (optional)"
                  {...register('description')}
                  placeholder="Optional description for internal context."
                  rows={3}
                />

                <div className="border-t pt-4 space-y-3">
                  <h3 className="font-semibold">Logo Options</h3>
                  <p className="text-sm text-muted-foreground">
                    Choose which logo to display at the top of the document. This affects the rendered HTML/PDF shell.
                  </p>
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        {...register('useOrgLogo')}
                        checked={!!useOrgLogo}
                        onCheckedChange={(checked) =>
                          setValue('useOrgLogo', checked === true, { shouldValidate: false })
                        }
                        disabled={!orgHasLogo}
                      />
                      <span className="text-sm">
                        Use client logo
                        {!orgHasLogo && (
                          <span className="ml-2 text-xs text-muted-foreground">(no logo uploaded yet)</span>
                        )}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        {...register('useClientLogo')}
                        checked={!!useClientLogo}
                        onCheckedChange={(checked) =>
                          setValue('useClientLogo', checked === true, { shouldValidate: false })
                        }
                        disabled={!selectedClientId}
                      />
                      <span className="text-sm">
                        Use project logo
                        {!selectedClientId && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            (select a project to see logo availability)
                          </span>
                        )}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      You can select both options to show both logos, or deselect both to show no logo.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Textarea
                  label="Markdown Content"
                  {...register('markdown', { required: 'Markdown content is required' })}
                  error={errors.markdown?.message}
                  placeholder={`Write your content in markdown...\n\nExamples:\n\n# Heading 1\n## Heading 2\n\n- Bullet item\n- Another item\n\n**Bold text** and _italic text_.`}
                  rows={18}
                />
              </div>
            </div>

            <div className="space-y-3 border-t pt-4">
              <h3 className="font-semibold">Live Preview (approximate)</h3>
              <p className="text-xs text-muted-foreground">
                This preview shows how your markdown will flow in the shell. The final PDF uses the same base layout and
                typography that the signing pipeline uses.
              </p>
              <div className="border rounded-lg bg-muted/40 p-4 max-h-[420px] overflow-auto">
                <div className="max-w-3xl mx-auto space-y-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-semibold">
                        {title?.trim() || 'Untitled Markdown Document'}
                      </span>
                    </div>
                    <div className="flex gap-2 text-xs text-muted-foreground">
                      {useOrgLogo && orgHasLogo && <span>Org logo</span>}
                      {useClientLogo && clientHasLogo && <span>Project logo</span>}
                      {(!useOrgLogo || !orgHasLogo) && (!useClientLogo || !clientHasLogo) && (
                        <span>No logo</span>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Project: {selectedClient ? selectedClient.name : 'Not selected'}
                  </div>
                  <div className="mt-2 rounded-md bg-white p-4 shadow-sm border text-sm whitespace-pre-wrap leading-relaxed">
                    {previewText}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => navigate('/documents')} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting || clients.length === 0}>
                Create Document
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
